// Leitura do XML de uma NFS-e do padrão nacional (leiaute 1.01) para autopreencher a tela de parâmetros
// fiscais. Só lê: quem aplica os valores na tela (e decide o que sobrescrever) é o componente.
//
// O XML tem dois blocos com nomes de tag repetidos — o da NFS-e (infNFSe, gerado pelo Sefin) e o da DPS
// (infNFSe/DPS/infDPS, enviado pelo emissor) — e o tomador também tem endereço. Por isso toda leitura é
// feita a partir do elemento pai certo, nunca com uma busca solta no documento.

export interface NfseXmlImport {
    number: string // número da NFS-e de origem, só para referência
    // DPS de origem: série, número e aplicativo que a emitiu (verAplic; o Zandu manda "ZanduPOS-...")
    dpsSerie: string
    dpsNumber: number | null
    dpsVerAplic: string
    // emitente (prestador)
    document: string
    legalName: string
    phone: string
    email: string
    municipalReg: string
    // endereço do emitente
    street: string // logradouro como veio no XML, ex.: "AVENIDA CESARIO ALVIM"
    addressNumber: string
    addressComplement: string
    neighborhood: string
    zipCode: string
    state: string
    cityName: string
    cityIbgeCode: string
    // regime
    opSimpNac: number // 1 = não optante, 2 = MEI, 3 = ME/EPP
    regApTribSN: number | null
    regEspTrib: number | null
    production: boolean
    // serviço
    codigoTributacaoNacional: string
    codigoTributacaoMunicipal: string
    nbs: string
    description: string
    tribISSQN: number | null
    tpRetISSQN: number | null
    aliquotaISSQN: number | null
    // IBS/CBS (grupo da DPS)
    ibscbs: {
        finNFSe: number | null
        indFinal: number | null
        cIndOp: string
        cst: string
        cClassTrib: string
    } | null
    // valor aproximado dos tributos (Lei 12.741), já em percentual
    totTribSN: number | null
    totTribFed: number | null
    totTribEst: number | null
    totTribMun: number | null
}

function child(parent: Element | null | undefined, ...path: string[]): Element | null {
    let current: Element | null = parent || null
    for (const name of path) {
        if (!current) return null
        current = Array.from(current.children).find((el) => el.localName === name) || null
    }
    return current
}

function text(parent: Element | null | undefined, ...path: string[]): string {
    return child(parent, ...path)?.textContent?.trim() || ''
}

function int(value: string): number | null {
    if (!value) return null
    const n = parseInt(value, 10)
    return Number.isFinite(n) ? n : null
}

function float(value: string): number | null {
    if (!value) return null
    const n = parseFloat(value)
    return Number.isFinite(n) ? n : null
}

export function parseNfseXml(xml: string): NfseXmlImport {
    const doc = new DOMParser().parseFromString(xml, 'application/xml')
    if (doc.getElementsByTagName('parsererror').length) {
        throw 'O arquivo não é um XML válido.'
    }
    const root = doc.documentElement
    const infNFSe = root?.localName === 'NFSe' ? child(root, 'infNFSe') : null
    const infDPS = child(infNFSe, 'DPS', 'infDPS')
    if (!infNFSe || !infDPS) {
        throw 'O arquivo não é o XML de uma NFS-e do padrão nacional. Use o XML da nota autorizada (não o da DPS nem o de um evento).'
    }

    const emit = child(infNFSe, 'emit')
    const enderNac = child(emit, 'enderNac')
    const prest = child(infDPS, 'prest')
    const regTrib = child(prest, 'regTrib')
    const cServ = child(infDPS, 'serv', 'cServ')
    const valores = child(infDPS, 'valores')
    const tribMun = child(valores, 'trib', 'tribMun')
    const totTrib = child(valores, 'trib', 'totTrib')
    const ibscbs = child(infDPS, 'IBSCBS')
    const gIBSCBS = child(ibscbs, 'valores', 'trib', 'gIBSCBS')

    // tributos aproximados: percentual direto (Simples ou por esfera) ou valores em R$, convertidos em
    // percentual sobre o valor do serviço. Valores todos zerados não informam nada e ficam de fora.
    let totTribSN = float(text(totTrib, 'pTotTribSN'))
    let totTribFed = float(text(totTrib, 'pTotTrib', 'pTotTribFed'))
    let totTribEst = float(text(totTrib, 'pTotTrib', 'pTotTribEst'))
    let totTribMun = float(text(totTrib, 'pTotTrib', 'pTotTribMun'))
    const vTotTrib = child(totTrib, 'vTotTrib')
    const vServ = float(text(valores, 'vServPrest', 'vServ')) || 0
    if (vTotTrib && vServ > 0) {
        const fed = float(text(vTotTrib, 'vTotTribFed')) || 0
        const est = float(text(vTotTrib, 'vTotTribEst')) || 0
        const mun = float(text(vTotTrib, 'vTotTribMun')) || 0
        if (fed + est + mun > 0) {
            const pct = (v: number) => Math.round((v / vServ) * 10000) / 100
            totTribFed = pct(fed)
            totTribEst = pct(est)
            totTribMun = pct(mun)
        }
    }

    return {
        number: text(infNFSe, 'nNFSe'),
        dpsSerie: text(infDPS, 'serie'),
        dpsNumber: int(text(infDPS, 'nDPS')),
        dpsVerAplic: text(infDPS, 'verAplic'),
        document: text(emit, 'CNPJ') || text(emit, 'CPF') || text(prest, 'CNPJ') || text(prest, 'CPF'),
        legalName: text(emit, 'xNome'),
        // o contato da DPS é o que a própria empresa informou; o do emit vem do cadastro nacional e pode
        // estar desatualizado (já vimos o de outra empresa lá)
        phone: text(prest, 'fone') || text(emit, 'fone'),
        email: text(prest, 'email') || text(emit, 'email'),
        municipalReg: text(prest, 'IM'),
        street: text(enderNac, 'xLgr'),
        addressNumber: text(enderNac, 'nro'),
        addressComplement: text(enderNac, 'xCpl'),
        neighborhood: text(enderNac, 'xBairro'),
        zipCode: text(enderNac, 'CEP'),
        state: text(enderNac, 'UF'),
        cityName: text(infNFSe, 'xLocEmi'),
        cityIbgeCode: text(enderNac, 'cMun') || text(infDPS, 'cLocEmi'),
        opSimpNac: int(text(regTrib, 'opSimpNac')) || 1,
        regApTribSN: int(text(regTrib, 'regApTribSN')),
        regEspTrib: int(text(regTrib, 'regEspTrib')),
        production: text(infDPS, 'tpAmb') === '1',
        codigoTributacaoNacional: text(cServ, 'cTribNac'),
        codigoTributacaoMunicipal: text(cServ, 'cTribMun'),
        nbs: text(cServ, 'cNBS'),
        description: text(cServ, 'xDescServ'),
        tribISSQN: int(text(tribMun, 'tribISSQN')),
        tpRetISSQN: int(text(tribMun, 'tpRetISSQN')),
        aliquotaISSQN: float(text(tribMun, 'pAliq')) ?? float(text(infNFSe, 'valores', 'pAliqAplic')),
        ibscbs: ibscbs
            ? {
                  finNFSe: int(text(ibscbs, 'finNFSe')),
                  indFinal: int(text(ibscbs, 'indFinal')),
                  cIndOp: text(ibscbs, 'cIndOp'),
                  cst: text(gIBSCBS, 'CST'),
                  cClassTrib: text(gIBSCBS, 'cClassTrib'),
              }
            : null,
        totTribSN,
        totTribFed,
        totTribEst,
        totTribMun,
    }
}
